import {
    Input,
    Select
} from "../../ui/components/Controls.js";

import {
    AdminEditorField
} from "./AdminEditorField.js";

import {
    applyHomeFeatureKindDefaults,
    homeFeatureKinds,
    readAdminHomeFeatureEditor,
    updateAdminHomeFeatureEditor,
    validateAdminHomeFeatureEditor
} from "./adminHomeFeatureEditor.js";

import type {
    AdminHomeFeatureEditorValue,
    HomeFeatureKind
} from "./adminHomeFeatureEditor.js";

import type {
    AdminContentEditorValue
} from "./adminContentEditor.js";

import {
    useAdminText
} from "./adminI18n.js";


/* -------------------------------------------------------------------------- */
/* Props                                                                      */
/* -------------------------------------------------------------------------- */

interface AdminHomeFeatureEditorFieldsProps {
    value: AdminContentEditorValue;

    onChange:
    (
        value: AdminContentEditorValue
    ) => void;
}


/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

function AdminHomeFeatureEditorFields({
    value,
    onChange
}: AdminHomeFeatureEditorFieldsProps) {
    const adminText =
        useAdminText();

    const feature =
        readAdminHomeFeatureEditor(
            value
        );

    const issues =
        validateAdminHomeFeatureEditor(
            feature
        );

    const errors =
        issues.filter(
            issue =>
                issue.severity
                === "error"
        );

    const warnings =
        issues.filter(
            issue =>
                issue.severity
                === "warning"
        );


    /* ---------------------------------------------------------------------- */
    /* Update                                                                 */
    /* ---------------------------------------------------------------------- */

    function change(
        next:
            AdminHomeFeatureEditorValue
    ): void {
        onChange(
            updateAdminHomeFeatureEditor(
                value,
                next
            )
        );
    }


    function update<
        K extends keyof AdminHomeFeatureEditorValue
    >(
        field: K,
        nextValue:
            AdminHomeFeatureEditorValue[K]
    ): void {
        change({
            ...feature,

            [field]:
                nextValue
        });
    }


    function changeKind(
        kind: HomeFeatureKind
    ): void {
        change(
            applyHomeFeatureKindDefaults(
                feature,
                kind
            )
        );
    }


    /* ---------------------------------------------------------------------- */
    /* Render                                                                 */
    /* ---------------------------------------------------------------------- */

    return (
        <div
            className="
                space-y-5
            "
        >
            {/* -------------------------------------------------------------- */}
            {/* Validation                                                     */}
            {/* -------------------------------------------------------------- */}

            <HomeFeatureValidation
                errors={errors}
                warnings={warnings}
            />


            {/* -------------------------------------------------------------- */}
            {/* Content type                                                   */}
            {/* -------------------------------------------------------------- */}

            <fieldset
                className="
                    space-y-4
                    rounded-card
                    border
                    border-line
                    p-4
                "
            >
                <legend
                    className="
                        px-2
                        text-base
                        font-bold
                        text-ink
                    "
                >
                    {adminText(
                        "Type de carte"
                    )}
                </legend>

                <AdminEditorField
                    label="Type éditorial"
                    required
                >
                    <Select
                        value={
                            feature.kind
                        }
                        onChange={
                            event =>
                                changeKind(
                                    event.target.value as HomeFeatureKind
                                )
                        }
                    >
                        {homeFeatureKinds.map(
                            option => (
                                <option
                                    key={
                                        option.value
                                    }
                                    value={
                                        option.value
                                    }
                                >
                                    {option.defaultIcon}
                                    {" "}
                                    {adminText(
                                        option.label
                                    )}
                                </option>
                            )
                        )}
                    </Select>
                </AdminEditorField>

                <p
                    className="
                        text-xs
                        leading-5
                        text-muted
                    "
                >
                    {adminText(
                        "Le type contrôle la catégorie éditoriale. Il ne change pas automatiquement la destination de la carte."
                    )}
                </p>
            </fieldset>


            {/* -------------------------------------------------------------- */}
            {/* French                                                         */}
            {/* -------------------------------------------------------------- */}

            <fieldset
                className="
                    space-y-4
                    rounded-card
                    border
                    border-line
                    p-4
                "
            >
                <legend
                    className="
                        px-2
                        text-base
                        font-bold
                        text-ink
                    "
                >
                    {adminText(
                        "Contenu français"
                    )}
                </legend>

                <div
                    className="
                        grid
                        gap-3
                        sm:grid-cols-2
                    "
                >
                    <AdminEditorField
                        label="Label"
                        hint="Petit texte au-dessus du titre"
                        required
                    >
                        <Input
                            required
                            value={
                                feature.labelFr
                            }
                            onChange={
                                event =>
                                    update(
                                        "labelFr",
                                        event.target.value
                                    )
                            }
                        />
                    </AdminEditorField>

                    <AdminEditorField
                        label="Titre"
                        required
                    >
                        <Input
                            required
                            value={
                                feature.titleFr
                            }
                            onChange={
                                event =>
                                    update(
                                        "titleFr",
                                        event.target.value
                                    )
                            }
                        />
                    </AdminEditorField>
                </div>

                <AdminEditorField
                    label="Information secondaire"
                    hint="Facultatif"
                >
                    <Input
                        placeholder="Expression · Niveau B1"
                        value={
                            feature.metaFr
                        }
                        onChange={
                            event =>
                                update(
                                    "metaFr",
                                    event.target.value
                                )
                        }
                    />
                </AdminEditorField>

                <p
                    className="
                        text-xs
                        leading-5
                        text-muted
                    "
                >
                    {adminText(
                        "L’information secondaire peut contenir un niveau, une durée, une catégorie ou une courte précision."
                    )}
                </p>
            </fieldset>


            {/* -------------------------------------------------------------- */}
            {/* Persian                                                        */}
            {/* -------------------------------------------------------------- */}

            <fieldset
                className="
                    space-y-4
                    rounded-card
                    border
                    border-line
                    p-4
                "
            >
                <legend
                    className="
                        px-2
                        text-base
                        font-bold
                        text-ink
                    "
                >
                    {adminText(
                        "Contenu persan"
                    )}
                </legend>

                <div
                    className="
                        grid
                        gap-3
                        sm:grid-cols-2
                    "
                >
                    <AdminEditorField
                        label="Label persan"
                    >
                        <Input
                            dir="rtl"
                            value={
                                feature.labelFa
                            }
                            onChange={
                                event =>
                                    update(
                                        "labelFa",
                                        event.target.value
                                    )
                            }
                        />
                    </AdminEditorField>

                    <AdminEditorField
                        label="Titre persan"
                    >
                        <Input
                            dir="rtl"
                            value={
                                feature.titleFa
                            }
                            onChange={
                                event =>
                                    update(
                                        "titleFa",
                                        event.target.value
                                    )
                            }
                        />
                    </AdminEditorField>
                </div>

                <AdminEditorField
                    label="Information secondaire persane"
                >
                    <Input
                        dir="rtl"
                        value={
                            feature.metaFa
                        }
                        onChange={
                            event =>
                                update(
                                    "metaFa",
                                    event.target.value
                                )
                        }
                    />
                </AdminEditorField>
            </fieldset>


            {/* -------------------------------------------------------------- */}
            {/* Presentation                                                   */}
            {/* -------------------------------------------------------------- */}

            <fieldset
                className="
                    space-y-4
                    rounded-card
                    border
                    border-line
                    p-4
                "
            >
                <legend
                    className="
                        px-2
                        text-base
                        font-bold
                        text-ink
                    "
                >
                    {adminText(
                        "Affichage"
                    )}
                </legend>

                <div
                    className="
                        grid
                        gap-3
                        sm:grid-cols-2
                    "
                >
                    <AdminEditorField
                        label="Icône"
                        hint="Emoji ou caractère court"
                        required
                    >
                        <Input
                            required
                            maxLength={12}
                            value={
                                feature.icon
                            }
                            onChange={
                                event =>
                                    update(
                                        "icon",
                                        event.target.value
                                    )
                            }
                        />
                    </AdminEditorField>

                    <AdminEditorField
                        label="Position"
                        hint="0 apparaît avant 10"
                        required
                    >
                        <Input
                            min="0"
                            max="999"
                            required
                            type="number"
                            value={
                                feature.position
                            }
                            onChange={
                                event =>
                                    update(
                                        "position",
                                        event.target.value
                                    )
                            }
                        />
                    </AdminEditorField>
                </div>


                {/* ---------------------------------------------------------- */}
                {/* Featured                                                   */}
                {/* ---------------------------------------------------------- */}

                <label
                    className="
                        flex
                        min-h-11
                        cursor-pointer
                        items-start
                        gap-3
                        rounded-control
                        border
                        border-line
                        px-3
                        py-3
                        text-sm
                        text-ink
                    "
                >
                    <input
                        checked={
                            feature.featured
                        }
                        className="
                            mt-0.5
                            h-5
                            w-5
                            shrink-0
                            accent-dino-600
                        "
                        type="checkbox"
                        onChange={
                            event =>
                                update(
                                    "featured",
                                    event.target.checked
                                )
                        }
                    />

                    <span>
                        <strong
                            className="
                                block
                            "
                        >
                            {adminText(
                                "Mettre cette carte en avant"
                            )}
                        </strong>

                        <span
                            className="
                                mt-1
                                block
                                text-xs
                                leading-5
                                text-muted
                            "
                        >
                            {adminText(
                                "Une carte mise en avant peut occuper toute la largeur de la grille."
                            )}
                        </span>
                    </span>
                </label>
            </fieldset>


            {/* -------------------------------------------------------------- */}
            {/* Navigation                                                     */}
            {/* -------------------------------------------------------------- */}

            <fieldset
                className="
                    space-y-4
                    rounded-card
                    border
                    border-line
                    p-4
                "
            >
                <legend
                    className="
                        px-2
                        text-base
                        font-bold
                        text-ink
                    "
                >
                    {adminText(
                        "Destination"
                    )}
                </legend>

                <AdminEditorField
                    label="Lien"
                    hint="Facultatif"
                >
                    <Input
                        placeholder="/journal/... ou https://..."
                        value={
                            feature.href
                        }
                        onChange={
                            event =>
                                update(
                                    "href",
                                    event.target.value
                                )
                        }
                    />
                </AdminEditorField>

                <div
                    className="
                        rounded-control
                        bg-line-soft
                        p-3
                        text-xs
                        leading-5
                        text-muted
                    "
                >
                    <p>
                        {adminText(
                            "Exemples de destinations :"
                        )}
                    </p>

                    <code
                        className="
                            mt-1
                            block
                            whitespace-pre-wrap
                            text-ink
                        "
                    >
                        {`/journal/2026-w37-jet-deau-geneve
/grammar/B1-G-027
/vocabulary
/travel`}
                    </code>
                </div>
            </fieldset>


            {/* -------------------------------------------------------------- */}
            {/* Scheduling                                                     */}
            {/* -------------------------------------------------------------- */}

            <fieldset
                className="
                    space-y-4
                    rounded-card
                    border
                    border-line
                    p-4
                "
            >
                <legend
                    className="
                        px-2
                        text-base
                        font-bold
                        text-ink
                    "
                >
                    {adminText(
                        "Programmation"
                    )}
                </legend>

                <div
                    className="
                        grid
                        gap-3
                        sm:grid-cols-2
                    "
                >
                    <AdminEditorField
                        label="Visible à partir du"
                        hint="Facultatif"
                    >
                        <Input
                            type="date"
                            value={
                                feature.startsOn
                            }
                            onChange={
                                event =>
                                    update(
                                        "startsOn",
                                        event.target.value
                                    )
                            }
                        />
                    </AdminEditorField>

                    <AdminEditorField
                        label="Visible jusqu’au"
                        hint="Facultatif"
                    >
                        <Input
                            type="date"
                            value={
                                feature.endsOn
                            }
                            onChange={
                                event =>
                                    update(
                                        "endsOn",
                                        event.target.value
                                    )
                            }
                        />
                    </AdminEditorField>
                </div>

                <div
                    className="
                        rounded-control
                        border
                        border-line
                        bg-line-soft
                        p-3
                        text-xs
                        leading-5
                        text-muted
                    "
                >
                    {adminText(
                        "Sans dates, la carte reste visible tant que cette révision est publiée. Avec une période, la Home décide automatiquement si elle doit être affichée."
                    )}
                </div>
            </fieldset>


            {/* -------------------------------------------------------------- */}
            {/* Preview                                                        */}
            {/* -------------------------------------------------------------- */}

            <fieldset
                className="
                    rounded-card
                    border
                    border-line
                    p-4
                "
            >
                <legend
                    className="
                        px-2
                        text-base
                        font-bold
                        text-ink
                    "
                >
                    {adminText(
                        "Aperçu"
                    )}
                </legend>

                <HomeFeaturePreview
                    feature={
                        feature
                    }
                />
            </fieldset>
        </div>
    );
}


/* -------------------------------------------------------------------------- */
/* Validation display                                                         */
/* -------------------------------------------------------------------------- */

interface HomeFeatureValidationProps {
    errors:
    ReturnType<
        typeof validateAdminHomeFeatureEditor
    >;

    warnings:
    ReturnType<
        typeof validateAdminHomeFeatureEditor
    >;
}


function HomeFeatureValidation({
    errors,
    warnings
}: HomeFeatureValidationProps) {
    const adminText =
        useAdminText();

    if (
        errors.length === 0
        && warnings.length === 0
    ) {
        return (
            <p
                className="
                    rounded-card
                    bg-emerald-50
                    p-3
                    text-sm
                    font-semibold
                    text-emerald-800
                "
            >
                {adminText(
                    "Carte prête à être publiée."
                )}
            </p>
        );
    }

    return (
        <div
            className="
                space-y-3
            "
        >
            {errors.length > 0 ? (
                <div
                    className="
                        rounded-card
                        border
                        border-danger
                        bg-danger-soft
                        p-3
                        text-sm
                        text-danger
                    "
                    role="alert"
                >
                    <strong>
                        {adminText(
                            "À corriger avant publication"
                        )}
                    </strong>

                    <ul
                        className="
                            mt-2
                            list-disc
                            space-y-1
                            ps-5
                        "
                    >
                        {errors.map(
                            (
                                issue,
                                index
                            ) => (
                                <li
                                    key={
                                        `${issue.field}:${index}`
                                    }
                                >
                                    <strong>
                                        {adminText(
                                            issue.field
                                        )}
                                        :
                                        {" "}
                                    </strong>

                                    {adminText(
                                        issue.message
                                    )}
                                </li>
                            )
                        )}
                    </ul>
                </div>
            ) : null}

            {warnings.length > 0 ? (
                <div
                    className="
                        rounded-card
                        border
                        border-amber-300
                        bg-amber-50
                        p-3
                        text-sm
                        text-amber-900
                    "
                >
                    <strong>
                        {adminText(
                            "À vérifier"
                        )}
                    </strong>

                    <ul
                        className="
                            mt-2
                            list-disc
                            space-y-1
                            ps-5
                        "
                    >
                        {warnings.map(
                            (
                                issue,
                                index
                            ) => (
                                <li
                                    key={
                                        `${issue.field}:${index}`
                                    }
                                >
                                    <strong>
                                        {adminText(
                                            issue.field
                                        )}
                                        :
                                        {" "}
                                    </strong>

                                    {adminText(
                                        issue.message
                                    )}
                                </li>
                            )
                        )}
                    </ul>
                </div>
            ) : null}
        </div>
    );
}


/* -------------------------------------------------------------------------- */
/* Preview                                                                    */
/* -------------------------------------------------------------------------- */

function HomeFeaturePreview({
    feature
}: {
    feature:
    AdminHomeFeatureEditorValue;
}) {
    const hasDestination =
        Boolean(
            feature.href.trim()
        );

    return (
        <article
            className={`
                overflow-hidden
                rounded-card
                border
                border-line
                bg-surface
                shadow-sm
                ${feature.featured
                    ? "max-w-3xl"
                    : "max-w-md"
                }
            `}
        >
            <div
                className={`
                    flex
                    items-center
                    justify-center
                    bg-dino-50
                    ${feature.featured
                        ? "h-36"
                        : "h-24"
                    }
                `}
            >
                <span
                    aria-hidden="true"
                    className={
                        feature.featured
                            ? "text-6xl"
                            : "text-5xl"
                    }
                >
                    {
                        feature.icon
                        || "✨"
                    }
                </span>
            </div>

            <div
                className="
                    p-4
                "
            >
                <p
                    className="
                        text-xs
                        font-bold
                        uppercase
                        tracking-wider
                        text-dino-700
                    "
                >
                    {
                        feature.labelFr
                        || "LABEL"
                    }
                </p>

                <h3
                    className="
                        mt-2
                        text-lg
                        font-semibold
                        leading-snug
                        text-ink
                    "
                >
                    {
                        feature.titleFr
                        || "Titre de la carte"
                    }
                </h3>

                {feature.metaFr ? (
                    <p
                        className="
                            mt-2
                            text-sm
                            text-muted
                        "
                    >
                        {feature.metaFr}
                    </p>
                ) : null}

                {hasDestination ? (
                    <p
                        className="
                            mt-3
                            truncate
                            font-mono
                            text-xs
                            text-dino-700
                        "
                    >
                        → {feature.href}
                    </p>
                ) : null}
            </div>
        </article>
    );
}


/* -------------------------------------------------------------------------- */
/* Export                                                                     */
/* -------------------------------------------------------------------------- */

export {
    AdminHomeFeatureEditorFields
};